import {Copy, ExternalLink, Clock, CheckCircle, Heart} from 'lucide-react'
import { useState, useEffect } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import RatingComponent from './RatingComponent'

interface CouponCardProps {
  title: string
  description: string
  code?: string
  discount: string
  expiryDate?: string
  verified?: boolean
  affiliateLink: string
  featured?: boolean
  couponId?: string
  onReveal?: () => void
  showRatings?: boolean
}

export default function CouponCard({
  title,
  description,
  code,
  discount,
  expiryDate,
  verified = true,
  affiliateLink,
  featured = false,
  couponId,
  onReveal,
  showRatings = false
}: CouponCardProps) {
  const [revealed, setRevealed] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const { user } = useAuth()

  // Check if coupon is saved on mount
  useEffect(() => {
    const checkSaved = async () => {
      if (!user || !couponId) return
      
      try {
        const prefs = await lumi.entities.userPreferences.list({
          filter: { userId: user.userId }
        })
        
        if (prefs.list.length > 0) {
          const userPrefs = prefs.list[0]
          setIsSaved(userPrefs.favoriteCoupons?.includes(couponId) || false)
        }
      } catch (error) {
        console.error('Error checking saved status:', error instanceof Error ? error.message : 'Unknown error', error)
      }
    }
    
    checkSaved()
  }, [user, couponId])

  const handleSave = async () => {
    if (!user) {
      toast.error('Please sign in to save coupons')
      return
    }
    
    if (!couponId) return
    
    try {
      const prefs = await lumi.entities.userPreferences.list({
        filter: { userId: user.userId }
      })
      
      if (prefs.list.length > 0) {
        const userPrefs = prefs.list[0]
        const favoriteCoupons = userPrefs.favoriteCoupons || []
        
        if (isSaved) {
          // Remove from favorites
          await lumi.entities.userPreferences.update(userPrefs.id, {
            favoriteCoupons: favoriteCoupons.filter((id: string) => id !== couponId)
          })
          setIsSaved(false)
          toast.success('Removed from saved coupons')
        } else {
          // Add to favorites
          await lumi.entities.userPreferences.update(userPrefs.id, {
            favoriteCoupons: [...favoriteCoupons, couponId]
          })
          setIsSaved(true)
          toast.success('Coupon saved!')
        }
      } else {
        // Create new preferences
        await lumi.entities.userPreferences.create({
          userId: user.userId,
          favoriteCoupons: [couponId],
          preferredBrands: [],
          preferredLocations: [],
          email: user.email,
          newsletterOptIn: false
        })
        setIsSaved(true)
        toast.success('Coupon saved!')
      }
    } catch (error) {
      console.error('Error saving coupon:', error instanceof Error ? error.message : 'Unknown error', error)
      toast.error('Failed to save coupon')
    }
  }

  const handleReveal = async () => {
    setRevealed(true)
    if (code) {
      navigator.clipboard.writeText(code)
      toast.success('Code copied to clipboard!')
    }
    
    // Track click in database
    if (couponId) {
      try {
        const result = await lumi.entities.coupons.list({
          filter: { _id: couponId }
        })
        if (result.list.length > 0) {
          const coupon = result.list[0]
          await lumi.entities.coupons.update(couponId, {
            clicks: (coupon.clicks || 0) + 1
          })
          onReveal?.()
        }
      } catch (error) {
        console.error('Error tracking click:', error instanceof Error ? error.message : 'Unknown error', error)
      }
    }
    
    // Open affiliate link in new tab
    window.open(affiliateLink, '_blank')
  }

  return (
    <>
      <Toaster position="top-center" />
      <div
        className={`bg-white rounded-xl border-2 border-dashed ${
          featured ? 'border-green-500 shadow-lg' : 'border-gray-300'
        } p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
            {verified && (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Verified</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className={`p-2 rounded-full transition-all ${
                isSaved
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-red-600'
              }`}
              title={isSaved ? 'Remove from saved' : 'Save coupon'}
            >
              <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
            </button>
            <div className="bg-gradient-to-br from-green-500 to-blue-500 text-white px-4 py-2 rounded-lg font-bold text-lg">
              {discount}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-4">{description}</p>

        {/* Expiry */}
        {expiryDate && (
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Clock className="w-4 h-4" />
            <span>Expires: {expiryDate}</span>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleReveal}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-lg flex items-center justify-center gap-2 transition-all ${
            revealed
              ? 'bg-gray-100 text-gray-700 border-2 border-gray-300'
              : 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700 shadow-md hover:shadow-lg'
          }`}
        >
          {revealed ? (
            <>
              {code ? (
                <>
                  <Copy className="w-5 h-5" />
                  <span className="font-mono">{code}</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-5 h-5" />
                  <span>Visit Site</span>
                </>
              )}
            </>
          ) : (
            <>
              {code ? 'Reveal Code' : 'Get Deal'}
              <ExternalLink className="w-5 h-5" />
            </>
          )}
        </button>

        {revealed && code && (
          <p className="text-center text-sm text-gray-500 mt-2">
            Code copied! Click again to revisit the site.
          </p>
        )}
      </div>
      {showRatings && couponId && <RatingComponent couponId={couponId} />}
    </>
  )
}
